import * as MediaFragment from "utils/MediaFragment";
import composeMediaFragment, {
  hasManifestForMediaFragment,
} from "utils/composeMediaFragment";
import getBrowserVersionFloat from "selectors/device/getBrowserVersionFloat";
import getCanPlayDash from "selectors/device/getCanPlayDash";
import getCanPlayFallbackDash from "selectors/device/getCanPlayFallbackDash";
import getCanPlayLegacyDash from "selectors/device/getCanPlayLegacyDash";
import getCanPlayHls from "selectors/device/getCanPlayHls";
import getCanPlayLegacyHls from "selectors/device/getCanPlayLegacyHls";
import getChapterDuration from "selectors/chapter/getChapterDuration";
import getUseNativeVideo from "selectors/device/getUseNativeVideo";
import getVideoRoot from "selectors/getVideoRoot";
import parseMediaType from "utils/parseMediaType";
import getUseDashFallbackFor360 from "selectors/device/getUseDashFallbackFor360";
import { createSelector } from "reselect";

import includes from "lodash/includes";
import last from "lodash/last";
import generateLocalFilenameFromUrl from "utils/generateLocalFilenameFromUrl";
import getIsIE from "selectors/device/getIsIE";
import getIsEdge from "selectors/device/getIsEdge";

const extractResolutionFromResolutionLabel = (resolutionLabel) => {
  const resolutionHeight = resolutionLabel.split("-")[0];
  return parseInt(resolutionHeight, 10);
};

const getDefaultMp4MediaFragment = (availableResolutionLabels) =>
  last(availableResolutionLabels) || MediaFragment.MP4_HIGH;

const getIsVideoOnly = (_, props) => props.forceMute;
const getIs360 = (state, props) =>
  !!state.story.entities.videos[props.videoId].is360;
//force the order ascending
const getAvailableResolutionLabels = (state, props) =>
  state.story.entities.videos[props.videoId].resolutions.sort(
    (a, b) =>
      extractResolutionFromResolutionLabel(b) <
      extractResolutionFromResolutionLabel(a),
  );
const getViewportHeight = (state) => state.viewport.dimensions.height;
const getManifestTypes = (state, props) =>
  state.story.entities.videos[props.videoId].manifestTypes || [];
const getIsLoop = (_, props) => props.loop;

const getResolutionForViewport = (availableResolutionLabels, viewportHeight) =>
  //take the first resolution to be greater than the viewport height
  availableResolutionLabels.filter(
    (resolutionLabel) =>
      extractResolutionFromResolutionLabel(resolutionLabel) >= viewportHeight,
  )[0];

const getForcedVideoResolution = (state, props) => {
  const offline = typeof window !== "undefined" && !!window.offline;

  if (!offline) return null;

  const videoEntity = state.story.entities.videos[props.videoId];

  const result =
    videoEntity.resolutions?.[0] ??
    state.story.entities.videos[state.story.video]?.resolutions?.[0];

  if (!result) {
    throw new Error("could not find resolution");
  }

  return result;
};

// This creates a new selector for each source that wants one. See the "Sharing Selectors Across
// Multiple Components" section of http://redux.js.org/docs/recipes/ComputingDerivedData.html
export const makeGetMediaSource = () =>
  createSelector(
    [
      getAvailableResolutionLabels,
      getBrowserVersionFloat,
      getCanPlayDash,
      getCanPlayFallbackDash,
      getCanPlayHls,
      getCanPlayLegacyDash,
      getCanPlayLegacyHls,
      getChapterDuration,
      getForcedVideoResolution,
      getIs360,
      getIsEdge,
      getIsIE,
      getIsLoop,
      getIsVideoOnly,
      getManifestTypes,
      getUseDashFallbackFor360,
      getUseNativeVideo,
      getVideoRoot,
      getViewportHeight,
    ],
    (
      availableResolutionLabels,
      browserVersionFloat,
      canPlayDash,
      canPlayFallbackDash,
      canPlayHls,
      canPlayLegacyDash,
      canPlayLegacyHls,
      chapterDuration,
      forcedVideoResolution,
      is360,
      isEdge,
      isIE,
      isLoop,
      isVideoOnly,
      manifestTypes,
      useDashFallbackFor360,
      useNativeVideo,
      videoRoot,
      viewportHeight,
    ) => {
      /******************************************
       *******************************************
       *   Selection logic :
       *       Can play hls (new encoding) and has hls?
       *           HEVC-HLS.m3u8
       *       Can play legacy hls and has legacy hls?
       *           Mobile-HLS.m3u8
       *       Can play dash (new encoding) and has dash?
       *           VP9-Dash.mpd
       *       Can play fallback dash and has fallback dash?
       *           H264-Dash.mpd
       *       Can play legacy dash and has legacy dash?
       *           Desktop-Dash.mpd
       *       Get best resolution of mp4 for the viewport?
       *           The resolution is in the file name so use
       *           that to select the correct based on the resolutions
       *       Get default resolution of mp4
       *******************************************/

      let fragment;

      function getMp4Fragment() {
        let nonLegacyMp4Fragment = getResolutionForViewport(
          availableResolutionLabels,
          viewportHeight,
        );

        //if found, use it, if not, take the largest because the viewport is larger.
        nonLegacyMp4Fragment =
          nonLegacyMp4Fragment ||
          getDefaultMp4MediaFragment(availableResolutionLabels);
        return composeMediaFragment(videoRoot, nonLegacyMp4Fragment);
      }
      if (forcedVideoResolution) {
        const mediaFragment = generateLocalFilenameFromUrl(
          composeMediaFragment(videoRoot, forcedVideoResolution),
        );

        return [
          {
            src: mediaFragment,
            type: parseMediaType(mediaFragment),
          },
        ];
      }

      // For IE11 and chapters shorter than 5s, use fallback mp4 instead of
      // dash. There's an issue with IE11 that causes dash videos to fail,
      // perhaps because of the segment size. For a five second video it doesn't
      // matter.
      if (
        parseInt(browserVersionFloat, 10) === 11 &&
        isIE &&
        chapterDuration &&
        chapterDuration < 5000
      ) {
        fragment = getMp4Fragment();
        return [fragment].map((result) => ({
          src: result,
          type: parseMediaType(result),
        }));
      }

      const isLegacyVideo =
        manifestTypes.length === 0 || availableResolutionLabels.length === 0;
      if (!isLegacyVideo) {
        if (
          canPlayHls &&
          hasManifestForMediaFragment(manifestTypes, MediaFragment.HLS)
        ) {
          fragment = composeMediaFragment(videoRoot, MediaFragment.HLS);
        } else if (
          canPlayLegacyHls &&
          hasManifestForMediaFragment(
            manifestTypes,
            MediaFragment.HLS_DESKTOP_LEGACY,
          )
        ) {
          if (
            hasManifestForMediaFragment(
              manifestTypes,
              MediaFragment.HLS_LOOP_LEGACY,
            ) &&
            isLoop
          ) {
            fragment = composeMediaFragment(
              videoRoot,
              MediaFragment.HLS_LOOP_LEGACY,
            );
          } else {
            fragment = composeMediaFragment(
              videoRoot,
              MediaFragment.HLS_DESKTOP_LEGACY,
            );
          }
        } else if (
          canPlayDash &&
          hasManifestForMediaFragment(manifestTypes, MediaFragment.DASH) &&
          !(useDashFallbackFor360 && is360)
        ) {
          fragment = composeMediaFragment(videoRoot, MediaFragment.DASH);
        } else if (
          canPlayFallbackDash &&
          hasManifestForMediaFragment(
            manifestTypes,
            MediaFragment.DASH_FALLBACK,
          )
        ) {
          fragment = composeMediaFragment(
            videoRoot,
            MediaFragment.DASH_FALLBACK,
          );
        } else if (
          canPlayLegacyDash &&
          hasManifestForMediaFragment(manifestTypes, MediaFragment.DASH_LEGACY)
        ) {
          fragment = composeMediaFragment(videoRoot, MediaFragment.DASH_LEGACY);
        } else {
          //take the first resolution to be greater than the viewport height
          fragment = getMp4Fragment();
        }
        const results = [fragment];

        return results.map((result) => ({
          src: result,
          type: parseMediaType(result),
        }));
      }

      //legacy video path

      const hasLoopManifest =
        manifestTypes.findIndex(
          (manifest) => manifest.toLowerCase().indexOf("desktop-hls") >= 0,
        ) >= 0;

      if (!useNativeVideo) {
        if (isVideoOnly) {
          // Don't compose this because it is an absolute url
          fragment = composeMediaFragment(
            videoRoot,
            MediaFragment.SILENT_AUDIO,
          );
        } else {
          fragment = composeMediaFragment(videoRoot, MediaFragment.AUDIO);
        }
      } else if (canPlayLegacyHls) {
        if (hasLoopManifest && isLoop) {
          fragment = composeMediaFragment(
            videoRoot,
            MediaFragment.HLS_LOOP_LEGACY,
          );
        } else {
          fragment = composeMediaFragment(
            videoRoot,
            MediaFragment.HLS_DESKTOP_LEGACY,
          );
        }
      } else if (canPlayLegacyDash) {
        fragment = composeMediaFragment(videoRoot, MediaFragment.DASH_LEGACY);
      } else {
        fragment = getMp4Fragment();
      }

      if (is360 && isIE && !isEdge) {
        fragment = composeMediaFragment(
          videoRoot,
          getDefaultMp4MediaFragment(availableResolutionLabels),
        );
      }

      const results = [fragment];

      const excludeFallbackUrls = [
        composeMediaFragment(videoRoot, MediaFragment.AUDIO),
        composeMediaFragment(videoRoot, MediaFragment.MP4_HIGH),
        composeMediaFragment(videoRoot, MediaFragment.MP4_LOW),
        composeMediaFragment(videoRoot, MediaFragment.MP4_MEDIUM),
        composeMediaFragment(videoRoot, MediaFragment.SILENT_AUDIO),
      ];

      if (!includes(excludeFallbackUrls, fragment)) {
        results.push(composeMediaFragment(videoRoot, MediaFragment.MP4_LOW));
      }

      return results.map((result) => ({
        src: result,
        type: parseMediaType(result),
      }));
    },
  );

export default makeGetMediaSource;
